import { useMemo, useState } from 'react';
import { FamilyMember } from '../types/family.types';
import { Event } from '../types/event.types';

interface SelectionButtonConfig {
  key: string;
  label: string;
  action: () => void;
  disabled?: boolean;
}

interface UseMemberSelectionReturn {
  selectedMembers: string[];
  selectedCount: number;
  selectionButtons: SelectionButtonConfig[];
  handleMemberToggle: (memberId: string) => void;
  handleSelectAll: () => void;
  handleClearAll: () => void;
  handleSelectAdults: () => void;
  handleSelectChildren: () => void;
  isSelected: (memberId: string) => boolean;
}

export const useMemberSelection = (
  eligibleMembers: FamilyMember[],
  selectedEvent?: Event | null,
): UseMemberSelectionReturn => {
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const handleMemberToggle = (memberId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  };

  const handleSelectAll = () => {
    const eligibleMemberIds = eligibleMembers.map((member) => member.id);
    setSelectedMembers(eligibleMemberIds);
  };

  const handleClearAll = () => {
    setSelectedMembers([]);
  };

  const handleSelectAdults = () => {
    const eligibleAdultIds = eligibleMembers.filter((member) => member.type === 'adult').map((member) => member.id);
    setSelectedMembers(eligibleAdultIds);
  };

  const handleSelectChildren = () => {
    const eligibleChildIds = eligibleMembers.filter((member) => member.type === 'child').map((member) => member.id);
    setSelectedMembers(eligibleChildIds);
  };

  const isSelected = (memberId: string): boolean => {
    return selectedMembers.includes(memberId);
  };

  const selectionButtons: SelectionButtonConfig[] = useMemo(
    () => [
      {
        key: 'selectAll',
        label: 'Select All',
        action: handleSelectAll,
      },
      {
        key: 'clearAll',
        label: 'Clear All',
        action: handleClearAll,
      },
      {
        key: 'adultsOnly',
        label: 'Adults Only',
        action: handleSelectAdults,
        disabled: !!selectedEvent && eligibleMembers.filter((m) => m.type === 'adult').length === 0,
      },
      {
        key: 'childrenOnly',
        label: 'Children Only',
        action: handleSelectChildren,
        disabled: !!selectedEvent && eligibleMembers.filter((m) => m.type === 'child').length === 0,
      },
    ],
    [eligibleMembers, selectedEvent],
  );

  return {
    selectedMembers,
    selectedCount: selectedMembers.length,
    selectionButtons,
    handleMemberToggle,
    handleSelectAll,
    handleClearAll,
    handleSelectAdults,
    handleSelectChildren,
    isSelected,
  };
};
